import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkles, X, Zap, ArrowUpRight, Type } from "lucide-react";

/**
 * Global 402 modal. Handles two cases:
 *   - INSUFFICIENT_CREDITS: AI credit wallet is empty for action X
 *   - WORD_LIMIT_REACHED / WORD_LIMIT_INSUFFICIENT: user hit monthly word quota
 */
export default function CreditsGuard() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onEvt = (e) => {
      if (!window.location.pathname.startsWith("/app")) return;
      setDetail(e.detail || {});
      setOpen(true);
    };
    window.addEventListener("bf:insufficient-credits", onEvt);
    return () => window.removeEventListener("bf:insufficient-credits", onEvt);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  if (!open) return null;

  const isWord = detail?.code === "WORD_LIMIT_REACHED" || detail?.code === "WORD_LIMIT_INSUFFICIENT";
  const plan = (detail?.plan || "free").toUpperCase();

  const title = isWord ? "Hai raggiunto il limite di parole" : "Hai terminato i tuoi crediti";
  const body = detail?.message || (isWord
    ? "Hai utilizzato tutte le parole incluse nel tuo piano questo mese. Passa a un piano superiore per continuare a generare testo."
    : "Hai utilizzato tutti i crediti AI inclusi nel tuo piano. Passa a un piano superiore per continuare a creare con BookForge.");
  const Icon = isWord ? Type : Zap;

  // Shared metrics
  const used = isWord ? (detail?.used ?? 0) : null;
  const monthly = isWord ? (detail?.monthly ?? 0) : null;
  const remaining = isWord ? (detail?.remaining ?? 0) : (detail?.available ?? 0);
  const required = detail?.required ?? 0;

  return (
    <div
      data-testid="credits-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog" aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}/>
      <div className="relative w-full max-w-md bg-[#111827] border border-white/[0.08] rounded-2xl shadow-[0_20px_60px_-20px_rgba(99,102,241,0.5)] p-6 bf-animate-in">
        <button
          data-testid="credits-modal-close"
          onClick={() => setOpen(false)}
          className="absolute right-3 top-3 p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-white/[0.05]"
        >
          <X className="w-4 h-4"/>
        </button>

        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center mb-4 shadow-[0_6px_18px_-4px_rgba(99,102,241,0.6)]">
          <Icon className="w-6 h-6 text-white"/>
        </div>

        <h2 data-testid="credits-modal-title" className="text-xl font-bold tracking-tight text-white mb-1">{title}</h2>
        <p data-testid="credits-modal-message" className="text-sm text-slate-400 mb-5 leading-relaxed">{body}</p>

        <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4 mb-5 space-y-2.5">
          <Row label="Piano attuale">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#A5B4FC] bg-[#6366F1]/10 border border-[#6366F1]/30 rounded-full px-2 py-0.5">
              <Sparkles className="w-3 h-3"/>{plan}
            </span>
          </Row>
          {isWord ? (
            <>
              <Row label="Parole utilizzate">
                <span data-testid="credits-modal-used" className="text-sm font-mono text-slate-200">{used.toLocaleString()}</span>
              </Row>
              <Row label="Limite parole">
                <span data-testid="credits-modal-monthly" className="text-sm font-mono text-slate-200">{monthly.toLocaleString()}</span>
              </Row>
              <Row label="Parole rimanenti">
                <span data-testid="credits-modal-remaining" className={`text-sm font-mono ${remaining > 0 ? "text-emerald-300" : "text-red-300"}`}>{remaining.toLocaleString()}</span>
              </Row>
              {!!required && (
                <Row label="Richieste">
                  <span data-testid="credits-modal-required" className="text-sm font-mono text-[#A5B4FC]">~{required.toLocaleString()}</span>
                </Row>
              )}
              {detail?.credits_needed != null && (
                <Row label="Crediti per parole extra">
                  <span data-testid="credits-modal-credits-needed" className="text-sm font-mono text-slate-200">{detail.credits_needed} / {detail.credits_available} disp.</span>
                </Row>
              )}
            </>
          ) : (
            <>
              <Row label="Crediti disponibili">
                <span data-testid="credits-modal-available" className="text-sm font-mono text-slate-200">{remaining}</span>
              </Row>
              <Row label="Crediti necessari">
                <span data-testid="credits-modal-required" className="text-sm font-mono text-[#A5B4FC]">{required}</span>
              </Row>
            </>
          )}
        </div>

        <div className="flex gap-2">
          <button
            data-testid="credits-modal-close-btn"
            onClick={() => setOpen(false)}
            className="bf-btn-outline flex-1 py-2.5 rounded-lg text-sm"
          >
            Chiudi
          </button>
          <button
            data-testid="credits-modal-upgrade"
            onClick={() => { setOpen(false); nav(isWord ? "/app/billing#credits" : "/app/billing"); }}
            className="bf-btn-gold flex-1 py-2.5 rounded-lg text-sm inline-flex items-center justify-center gap-1.5"
          >
            {isWord ? "Acquista crediti" : "Visualizza i piani"} <ArrowUpRight className="w-4 h-4"/>
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-400">{label}</span>
      {children}
    </div>
  );
}
